import { useState, useCallback } from 'react';
import { HISTORY_LIMIT } from '../config/constants';

/**
 * A custom hook to manage undo/redo history for state.
 * @param {any} initialPresent - The initial state
 * @param {number} limit - Maximum number of history states to keep
 */
export function useHistory(initialPresent, limit = HISTORY_LIMIT) {
  const [state, setState] = useState({
    past: [],
    present: initialPresent,
    future: []
  });

  const canUndo = state.past.length > 0;
  const canRedo = state.future.length > 0;

  const undo = useCallback(() => {
    setState(currentState => {
      if (currentState.past.length === 0) return currentState;

      const previous = currentState.past[currentState.past.length - 1];
      const newPast = currentState.past.slice(0, currentState.past.length - 1);

      return {
        past: newPast,
        present: previous,
        future: [currentState.present, ...currentState.future]
      };
    });
  }, []);

  const redo = useCallback(() => {
    setState(currentState => {
      if (currentState.future.length === 0) return currentState;

      const next = currentState.future[0];
      const newFuture = currentState.future.slice(1);

      return {
        past: [...currentState.past, currentState.present],
        present: next,
        future: newFuture
      };
    });
  }, []);

  const setPresent = useCallback((newPresentOrUpdater) => {
    setState(currentState => {
      const newPresent = typeof newPresentOrUpdater === 'function'
        ? newPresentOrUpdater(currentState.present)
        : newPresentOrUpdater;
      if (newPresent === currentState.present) return currentState;

      const newPast = [...currentState.past, currentState.present];
      if (newPast.length > limit) {
        newPast.shift();
      }

      return {
        past: newPast,
        present: newPresent,
        future: [],
      };
    });
  }, [limit]);

  const reset = useCallback((newPresent) => {
    setState({
      past: [],
      present: newPresent,
      future: []
    });
  }, []);

  return {
    state: state.present,
    set: setPresent,
    undo,
    redo,
    canUndo,
    canRedo,
    reset
  };
}
